import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Select,
  Button,
  Table,
  Tag,
} from "antd";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller, useWatch } from "react-hook-form";
import { BookingData } from "../../types/booking";
import { toast } from "react-hot-toast";
import {
  RefundRequestFormData,
  refundRequestSchema,
} from "../../lib/schemas/refundRequestSchema";
import { useStore } from "../../app/stores/store";
import { RefundStatus } from "../../lib/utils";
import dayjs from "dayjs";
import { Typography } from "antd";

type Props = {
  booking: BookingData | null;
  onClose: () => void;
  open: boolean;
  onRefundSuccess?: () => void;
};

const RefundRequestModal: React.FC<Props> = ({
  booking,
  onClose,
  open,
  onRefundSuccess,
}) => {
  if (!open || !booking) return null;

  const { bookingStore } = useStore();
  const hasClients = booking.clients && booking.clients.length > 0;

  const existingRequests = booking.refundRequests ?? [];
  const { Text } = Typography;

  // If no clients → don't allow refund request
  if (!hasClients) {
    return (
      <Modal
        title={`Request Refund for ${booking.serviceName}`}
        open={open}
        onCancel={onClose}
        footer={null}
        destroyOnClose
      >
        <p style={{ color: "red" }}>
          This booking has no clients. Refund requests can only be made for
          clients.
        </p>
      </Modal>
    );
  }

  // Track which clients already have refund requests
  const disabledClientIds = new Set(
    existingRequests.flatMap((r) =>
      r.refundRequestClients.map((rc) => rc.client.id)
    )
  );

  // Check if all clients already have refund requests
  const allClientsRequested = booking.clients?.every((c) =>
    disabledClientIds.has(c.id)
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<RefundRequestFormData>({
    resolver: zodResolver(refundRequestSchema),
    defaultValues: {
      bookingId: booking.id,
      exitDate: "",
      remarks: "",
      requestedAmount: undefined,
      clientIds: [],
    },
  });

  // ✅ Auto-calculation: per-client refund amount
  //const perClientAmount = Number(booking.securityDeposit ?? 0);

const perClientAmount = (
  booking.includeSecurityDeposit && 
  Number(booking.customSecurityDeposit) > 0
)
  ? Number(booking.customSecurityDeposit)
  : Number(booking.securityDeposit ?? 0);


  // Watch selected clients
  const clientIds = useWatch({ control, name: "clientIds" });
  const selectedCount = clientIds?.length ?? 0;
  const totalAmount = perClientAmount * selectedCount;

  // Always set per-client amount in the hidden field
  useEffect(() => {
    setValue("requestedAmount", perClientAmount, { shouldValidate: true });
  }, [perClientAmount, setValue]);

  const onSubmit = async (data: RefundRequestFormData) => {
    try {
      if (
        !data.exitDate ||
        !data.requestedAmount ||
        (booking.clients!.length > 0 &&
          (!data.clientIds || data.clientIds.length === 0))
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      await bookingStore.createRefundRequest(data);
      toast.success("Refund request submitted!");
      onRefundSuccess?.();
      reset();
      onClose();
    } catch {
      toast.error("Failed to submit refund request.");
    }
  };

  // Table columns for existing requests
  const columns = [
    { title: "Client", dataIndex: "clientName", key: "clientName" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: RefundStatus) => {
        let color = "blue";
        if (status === RefundStatus.Approved) color = "green";
        else if (status === RefundStatus.Rejected) color = "red";
        else if (status === RefundStatus.Pending) color = "orange";
        return <Tag color={color}>{RefundStatus[status]}</Tag>;
      },
    },
  ];

  // Prepare refund requests table data
  const requestTableData = existingRequests.flatMap((r) =>
    r.refundRequestClients.map((rc) => ({
      id: rc.id,
      clientName: rc.client?.name ?? "—",
      status: r.status,
    }))
  );

  return (
    <Modal
      title={`Request Refund for ${booking.serviceName}`}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={700}
    >
      {/* Show existing requests */}
      {requestTableData.length > 0 && (
        <>
          <h3>Existing Refund Requests</h3>
          <Table
            columns={columns}
            dataSource={requestTableData}
            rowKey="id"
            pagination={false}
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      {/* If all clients have refund requests */}
      {allClientsRequested && (
        <p style={{ color: "red", marginBottom: 20 }}>
          All clients in this booking already have refund requests.
        </p>
      )}

      {/* Refund form */}
      {!allClientsRequested && (
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <Controller
            name="bookingId"
            control={control}
            render={({ field }) => <input type="hidden" {...field} />}
          />
          {/* Hidden per-client amount */}
          <Controller
            name="requestedAmount"
            control={control}
            render={({ field }) => (
              <input type="hidden" {...field} value={perClientAmount} />
            )}
          />
          <Form.Item
            label="Exit Date"
            validateStatus={errors.exitDate ? "error" : ""}
            help={errors.exitDate?.message}
          >
            <Controller
              name="exitDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : "")
                  }
                  showTime
                  format="YYYY-MM-DD HH:mm"
                  style={{ width: "100%" }}
                />
              )}
            />
          </Form.Item>
          {/* Display total refund amount */}
          <Form.Item
            label="Total Refund Amount (AED)"
            help={
              selectedCount > 0
                ? `Calculated: ${selectedCount} × ${perClientAmount} = ${totalAmount} AED`
                : "Select clients to calculate total."
            }
          >
            <InputNumber
              value={totalAmount}
              disabled
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="Remarks"
            validateStatus={errors.remarks ? "error" : ""}
            help={errors.remarks?.message}
          >
            <Controller
              name="remarks"
              control={control}
              render={({ field }) => (
                <>
                  <Input.TextArea
                    {...field}
                    rows={4}
                    placeholder="Additional notes"
                  />
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    💡If you are inside UAE and want to transfer by bank, please
                    include your IBAN in the remarks.
                  </Text>
                </>
              )}
            />
          </Form.Item>
          <Form.Item
            label="Clients"
            validateStatus={errors.clientIds ? "error" : ""}
            help={errors.clientIds?.message}
          >
            <Controller
              name="clientIds"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  mode="multiple"
                  placeholder="Select clients"
                  options={booking.clients!.map((client) => ({
                    label: client.name,
                    value: client.id,
                    disabled: disabledClientIds.has(client.id),
                  }))}
                  onChange={(vals) => field.onChange(vals)} // <-- important
                />
              )}
            />
          </Form.Item>
          <div className="text-right mt-4">
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Submit Refund
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default RefundRequestModal;
